/**
 * InstallPanel — the three ways a finished signature gets into Gmail:
 *
 *  1. Install to Gmail: calls the applyGmailSignature Cloud Function, which
 *     writes the HTML straight into the user's Gmail sendAs settings via the
 *     Gmail API (bypasses the paste sanitizer entirely — most reliable).
 *  2. Copy signature: rich dual-MIME clipboard (text/html + text/plain,
 *     precomputed so the write stays inside the user gesture) for pasting
 *     into Gmail's signature box. Hidden-selection execCommand fallback.
 *  3. Download .html: the classic open-in-browser / select-all / copy path.
 *
 * Plus step-by-step instructions and the Gmail-app mobile-signature warning.
 */

import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Kbd,
  Link,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  OrderedList,
  Spinner,
  Text,
  Tooltip,
  UnorderedList,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiCheckCircle, FiCode, FiCopy, FiDownload, FiExternalLink, FiSend } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { compileSignatureHtml, compileSignatureText } from './compileSignatureHtml';

const GMAIL_SETTINGS_URL = 'https://mail.google.com/mail/u/0/#settings/general';

function downloadHtmlFile(html, name) {
  const docHtml = `<!doctype html><html><head><meta charset="utf-8"><title>${name || 'Signature'}</title></head><body style="margin:24px;background:#ffffff;">${html}</body></html>`;
  const blob = new Blob([docHtml], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(name || 'gmail-signature').replace(/[^\w-]+/g, '-').toLowerCase()}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Legacy fallback: render off-screen, select, execCommand('copy'). */
function selectionCopy(html) {
  const holder = document.createElement('div');
  holder.style.position = 'fixed';
  holder.style.left = '-99999px';
  holder.style.top = '0';
  holder.contentEditable = 'true';
  holder.innerHTML = html;
  document.body.appendChild(holder);
  const range = document.createRange();
  range.selectNodeContents(holder);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (e) {
    ok = false;
  }
  selection.removeAllRanges();
  document.body.removeChild(holder);
  return ok;
}

export default function InstallPanel({ spec, stats }) {
  const toast = useToast();
  const installModal = useDisclosure();
  const panelBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const previewBG = useColorModeValue('gray.50', 'gray.700');

  const [installing, setInstalling] = useState(false);
  const [currentSig, setCurrentSig] = useState({ loading: false, html: null, error: null });
  const [installed, setInstalled] = useState(false);

  // Precomputed payloads: clipboard writes must stay inside the user gesture
  // (iOS Safari allows ~100ms), so never compile during the click.
  const html = useMemo(() => compileSignatureHtml(spec), [spec]);
  const plainText = useMemo(() => compileSignatureText(spec), [spec]);

  const disabledReason = !html
    ? 'The signature is empty.'
    : stats?.overLimit
      ? `Over Gmail's 10,000-character limit by ${(stats.chars - stats.limit).toLocaleString()} — remove or simplify blocks first.`
      : null;

  const copyRich = async () => {
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else if (!selectionCopy(html)) {
        throw new Error('Clipboard access unavailable');
      }
      toast({
        title: 'Signature copied',
        description: 'Paste it into Gmail\'s signature box with Cmd+V (not "paste and match style").',
        status: 'success',
        duration: 5000,
      });
    } catch (err) {
      if (selectionCopy(html)) {
        toast({ title: 'Signature copied', status: 'success', duration: 4000 });
      } else {
        toast({ title: 'Copy failed', description: err.message, status: 'error', duration: 5000 });
      }
    }
  };

  const copyHtmlSource = async () => {
    try {
      await navigator.clipboard.writeText(html);
      toast({ title: 'HTML source copied', status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Copy failed', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const openInstallModal = async () => {
    installModal.onOpen();
    setInstalled(false);
    setCurrentSig({ loading: true, html: null, error: null });
    try {
      const functions = getFunctions(app, 'us-east1');
      const fetchSignature = httpsCallable(functions, 'fetchUserSignature');
      const result = await fetchSignature({});
      setCurrentSig({ loading: false, html: result.data?.signature || '', error: null });
    } catch (err) {
      setCurrentSig({ loading: false, html: null, error: err.message });
    }
  };

  const confirmInstall = async () => {
    setInstalling(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const apply = httpsCallable(functions, 'applyGmailSignature');
      const result = await apply({ html });
      if (result.data?.success) {
        setInstalled(true);
        toast({
          title: 'Installed into Gmail',
          description: 'Send yourself a test email to confirm it looks right.',
          status: 'success',
          duration: 6000,
        });
      } else {
        throw new Error(result.data?.message || 'Install failed');
      }
    } catch (err) {
      toast({ title: 'Install failed', description: err.message, status: 'error', duration: 8000 });
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Box bg={panelBG} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4}>
      <Text fontSize="sm" fontWeight="bold" mb={3}>Install your signature</Text>

      {disabledReason && (
        <Alert status={stats?.overLimit ? 'error' : 'info'} borderRadius="md" mb={3} fontSize="sm" py={2}>
          <AlertIcon />
          {disabledReason}
        </Alert>
      )}

      <Flex wrap="wrap" style={{ gap: '8px' }}>
        <Tooltip label="Writes the signature directly into your Gmail settings — no copy/paste needed." hasArrow>
          <Button size="sm" colorScheme="purple" leftIcon={<FiSend />} onClick={openInstallModal} isDisabled={!!disabledReason}>
            Install to Gmail
          </Button>
        </Tooltip>
        <Tooltip label="Copies the rendered signature for pasting into Gmail's signature box." hasArrow>
          <Button size="sm" variant="outline" leftIcon={<FiCopy />} onClick={copyRich} isDisabled={!!disabledReason}>
            Copy signature
          </Button>
        </Tooltip>
        <Tooltip label="Copies the raw HTML source (for other tools)." hasArrow>
          <Button size="sm" variant="ghost" leftIcon={<FiCode />} onClick={copyHtmlSource} isDisabled={!html}>
            Copy HTML
          </Button>
        </Tooltip>
        <Tooltip label="Download as an .html file you can open in a browser." hasArrow>
          <Button size="sm" variant="ghost" leftIcon={<FiDownload />} onClick={() => downloadHtmlFile(html, spec?.name)} isDisabled={!html}>
            Download
          </Button>
        </Tooltip>
      </Flex>

      <Accordion allowToggle mt={4} borderColor={borderColor}>
        <AccordionItem>
          <AccordionButton px={2}>
            <Text flex={1} textAlign="left" fontSize="xs" fontWeight="semibold">How to paste it into Gmail (if you don't use one-click install)</Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={2} fontSize="xs" color={mutedColor}>
            <OrderedList spacing={1} pl={1}>
              <ListItem>Click <b>Copy signature</b> above.</ListItem>
              <ListItem>
                Open <Link color="purple.400" href={GMAIL_SETTINGS_URL} isExternal>Gmail Settings → General <FiExternalLink style={{ display: 'inline' }} /></Link> and scroll to <b>Signature</b>.
              </ListItem>
              <ListItem>Click <b>Create new</b>, name it, then click into the signature box.</ListItem>
              <ListItem>Paste with <Kbd>Cmd</Kbd>+<Kbd>V</Kbd> (Windows: <Kbd>Ctrl</Kbd>+<Kbd>V</Kbd>). Never use "Paste and match style" — it strips all formatting.</ListItem>
              <ListItem>Set BOTH <b>Signature defaults</b> dropdowns ("for new emails" and "on reply/forward") — they default to "No signature".</ListItem>
              <ListItem>Scroll down and click <b>Save Changes</b>.</ListItem>
            </OrderedList>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <AccordionButton px={2}>
            <Text flex={1} textAlign="left" fontSize="xs" fontWeight="semibold">Gmail phone app — important</Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={2} fontSize="xs" color={mutedColor}>
            <Text mb={2}>
              The Gmail app has its own plain-text <b>Mobile Signature</b> setting. If it's set, it overrides (or doubles) this signature on emails sent from your phone.
            </Text>
            <Text>
              <b>Leave it OFF</b>: Gmail app → Settings → your account → Mobile Signature → clear it. The app then automatically uses this rich signature.
            </Text>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <AccordionButton px={2}>
            <Text flex={1} textAlign="left" fontSize="xs" fontWeight="semibold">After installing — 60-second checklist</Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={2} fontSize="xs" color={mutedColor}>
            <UnorderedList spacing={1} pl={1}>
              <ListItem>Send yourself a real email — the settings-box preview is not real rendering.</ListItem>
              <ListItem>Reply to that email and check how the signature looks quoted/indented.</ListItem>
              <ListItem>Check it on your phone in dark mode.</ListItem>
              <ListItem>Never edit the signature inside Gmail's box — make changes here and reinstall.</ListItem>
              <ListItem>Bonus: emails the CRM sends for you automatically use this same Gmail signature.</ListItem>
            </UnorderedList>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {/* Install confirmation modal */}
      <Modal isOpen={installModal.isOpen} onClose={installModal.onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Install to Gmail</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {installed ? (
              <Alert status="success" borderRadius="md">
                <AlertIcon as={FiCheckCircle} />
                <Box>
                  <Text fontWeight="bold" fontSize="sm">Signature installed.</Text>
                  <Text fontSize="sm" mt={1}>
                    One thing to check once: in{' '}
                    <Link color="purple.500" href={GMAIL_SETTINGS_URL} isExternal>Gmail Settings → Signature defaults</Link>,
                    make sure your signature is selected for new emails and replies. Then send yourself a test email.
                  </Text>
                </Box>
              </Alert>
            ) : (
              <>
                <Text fontSize="sm" mb={3}>
                  This replaces the signature on your primary Gmail address with the one you built here.
                </Text>
                <Text fontSize="xs" fontWeight="bold" mb={1}>Currently in your Gmail:</Text>
                <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3} mb={3} bg={previewBG} maxH="150px" overflowY="auto">
                  {currentSig.loading && <Spinner size="sm" />}
                  {!currentSig.loading && currentSig.error && (
                    <Text fontSize="xs" color="orange.400">Couldn't read your current signature ({currentSig.error}) — installing will still work.</Text>
                  )}
                  {!currentSig.loading && !currentSig.error && !currentSig.html && (
                    <Text fontSize="xs" color={mutedColor} fontStyle="italic">No signature currently set.</Text>
                  )}
                  {!currentSig.loading && currentSig.html && (
                    <Box bg="white" p={2} borderRadius="sm">
                      {/* eslint-disable-next-line react/no-danger */}
                      <div dangerouslySetInnerHTML={{ __html: currentSig.html }} />
                    </Box>
                  )}
                </Box>
                <Text fontSize="xs" fontWeight="bold" mb={1}>Will be replaced with:</Text>
                <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3} bg="white" maxH="220px" overflowY="auto">
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: html }} />
                </Box>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {installed ? (
              <Button size="sm" onClick={installModal.onClose}>Done</Button>
            ) : (
              <HStack spacing={2}>
                <Button size="sm" variant="ghost" onClick={installModal.onClose}>Cancel</Button>
                <Button size="sm" colorScheme="purple" leftIcon={<FiSend />} onClick={confirmInstall} isLoading={installing} loadingText="Installing…">
                  Replace my Gmail signature
                </Button>
              </HStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
